import { useListCalculators } from "#hooks/adapters/useCalculators";
import { useListEquipmentTypes } from "#hooks/adapters/useEquipment";
import {
  ESTIMATION_METHODS_QUERY_KEY,
  useBulkUpdateEstimationMethodStatus,
  useExportEstimationMethods,
  useListEstimationMethods,
} from "#hooks/adapters/useEstimationMethods";
import { useListFlowTypes } from "#hooks/adapters/useFlows";
import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { linkToFacilityDetail } from "#routes/organization/facilities/[facilityId]";
import { ImportDataAction } from "#src/batteries-included-components";
import { BulkUpdateProgressBanner } from "#src/batteries-included-components/Banners/BulkUpdateProgressBanner/BulkUpdateProgressBanner";
import PermissionAwareExportButton from "#src/batteries-included-components/Buttons/PermissionAwareExportButton/PermissionAwareExportButton";
import { UpdateEntityStatusDialog } from "#src/batteries-included-components/Dialogs/UpdateEntityStatusDialog";
import {
  EstimationMethodDetailFilterConfigFilterType,
  EstimationMethodDetailViewConfigFilterType,
  EstimationMethodsTableFilterArea,
  EstimationMethodTableTitleDecorationFilterArea,
} from "#src/batteries-included-components/FilterAreas/EstimationMethodsFilterAreas/EstimationMethodsFilterAreas";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import {
  linkToAssetDetailPage,
  linkToEstimationMethodDetailPage,
} from "#utils/links";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrayDataDisplay,
  Button,
  DataTable,
  DataTablePanel,
  HeaderType,
  Pill,
  StorageKeys,
} from "@validereinc/common-components";
import {
  AssetType,
  AssetTypeType,
  EstimationMethodStatus,
  EstimationMethodType,
  ResourceDefinitions,
  SortDirection,
  type EstimationMethodAdapter,
} from "@validereinc/domain";
import {
  toFlattenedObject,
  toTitleCaseString,
  yearMonthFromISO,
} from "@validereinc/utilities";
import React, { useState } from "react";

export const EstimationMethodListTablePanel = ({
  filterConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
  viewConfigStorageKey,
  assetType,
}: Pick<
  StorageKeys,
  | "filterConfigStorageKey"
  | "tableConfigStorageKey"
  | "viewConfigStorageKey"
  | "tableStateStorageKey"
> & {
  assetType: AssetTypeType;
}) => {
  const queryClient = useQueryClient();
  const isFilteredByEntity = !!assetType;
  const { localize } = useLocalization();
  const [isDataIngestionEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:data_pipeline",
  });
  const equipmentTypes = useListEquipmentTypes({}).data?.data ?? [];
  const flowTypesQuery = useListFlowTypes();
  const calculators = useListCalculators().data?.calculators ?? [];
  const [filters] =
    useStickyState<EstimationMethodDetailFilterConfigFilterType>(
      {},
      filterConfigStorageKey
    );
  const [{ month }] =
    useStickyState<EstimationMethodDetailViewConfigFilterType>(
      {},
      viewConfigStorageKey
    );
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "name",
      sortDirection: SortDirection.ASCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const [selectedEstimationMethods, setSelectedEstimationMethods] = useState<
    Record<string, EstimationMethodType>
  >({});
  const [isSelectionInverted, setIsSelectionInverted] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [
    isUpdateEstimationStatusDialogOpen,
    setIsUpdateEstimationStatusDialogOpen,
  ] = useState(false);

  const estimationMethodParams: Parameters<
    typeof EstimationMethodAdapter.getList
  >[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      ...toFlattenedObject(filters),
      ...(isFilteredByEntity
        ? { entity_type: { $exact: assetType } as any }
        : { entity_type: [AssetType.EQUIPMENT, AssetType.FLOW] }),
      ...(filters.status
        ? {
            status: filters.status,
          }
        : {}),
    },
    meta: {
      ...(month?.from ? { period: yearMonthFromISO(month.from) } : {}),
    },
  };

  const { data, isLoading } = useListEstimationMethods(estimationMethodParams);
  const { mutate: exportEstimationMethods, isLoading: isExporting } =
    useExportEstimationMethods(estimationMethodParams);

  const [bulkUpdateEstimationMethodStatusJobStorageKey] = useStorageKey(
    ["async-job"],
    `estimation-methods-bulk-update-list-${assetType}`
  );

  const { handleBulkUpdateEstimationMethodStatus } =
    useBulkUpdateEstimationMethodStatus({
      jobKey: bulkUpdateEstimationMethodStatusJobStorageKey,
      // @ts-expect-error TODO: Typing on filter
      filters: estimationMethodParams.filters,
      selectedEstimationMethods,
      isSelectionInverted,
      setIsUpdating,
      invalidate: () => {
        queryClient.invalidateQueries({
          queryKey: [
            ESTIMATION_METHODS_QUERY_KEY,
            "list",
            estimationMethodParams,
          ],
        });
      },
    });

  const headers: Array<HeaderType<EstimationMethodType>> = [
    {
      label: "Estimation Method",
      key: "name",
      isSortable: true,
      renderComponent: ({ item }) => (
        <RoutingLink
          to={linkToEstimationMethodDetailPage(
            item.entity_type,
            item.entity_id,
            item.id
          )}
        >
          {item.name}
        </RoutingLink>
      ),
    },
    {
      label: "Status",
      key: "status",
      isSortable: true,
      renderComponent: ({ item }: { item: EstimationMethodType }) => {
        return (
          <Pill
            variant={
              item.status === EstimationMethodStatus.ACTIVE
                ? "success"
                : "default"
            }
          >
            {toTitleCaseString(item.status, {
              intent: "ui",
              exceptions: ["oil-and-gas"],
            })}
          </Pill>
        );
      },
    },
    {
      label: "Reporting Scenario",
      key: "reporting_groups",
      renderComponent: ({ item: { reporting_groups: reportingGroups } }) => (
        <ArrayDataDisplay value={reportingGroups?.map(({ name }) => name)} />
      ),
    },
    {
      label: isFilteredByEntity
        ? "Asset"
        : toTitleCaseString(assetType, { intent: "ui" }),
      key: "equipment.name",
      renderComponent: ({ item }) => {
        const equipmentLink = linkToAssetDetailPage(
          item.entity_type,
          item.entity_id
        );
        const equipmentName =
          (item as any).equipment?.name ?? (item as any).flow?.name;

        return equipmentLink && equipmentName ? (
          <RoutingLink to={equipmentLink}>{equipmentName}</RoutingLink>
        ) : (
          "-"
        );
      },
    },
    ...(isFilteredByEntity
      ? [
          {
            label: "Asset Type",
            key: "entity_type",
            isSortable: true,
            renderComponent: ({ item }: any) =>
              localize(item?.entity_type) ?? "-",
          },
        ]
      : []),
    {
      label: isFilteredByEntity
        ? "Asset Subtype"
        : `${toTitleCaseString(assetType, { intent: "ui" })} Type`,
      key: "equipment.type_id",
      renderComponent: ({ item }) => {
        const getText = () => {
          switch (item.entity_type) {
            case AssetType.EQUIPMENT:
              return equipmentTypes.find(
                ({ id }) => id === (item as any).equipment?.type_id
              )?.name;
            case AssetType.FLOW:
              return flowTypesQuery.data?.data.find(
                ({ id }) => id === (item as any).flow?.type
              )?.name;
            default:
              return toTitleCaseString(
                (item as any)?.equipment?.type_id ?? (item as any).flow?.type,
                { intent: "ui" }
              );
          }
        };

        return <DataTable.DataRow.TextCell value={getText()} />;
      },
    },
    {
      label: "Facility",
      key: "equipment.facility.name",
      renderComponent: ({ item }) => {
        const facilityId =
          (item as any).flow?.associated_facility_id ??
          (item as any).equipment?.facility_id;
        const facilityName =
          (item as any).flow?.associated_facility?.name ??
          (item as any).equipment?.facility?.name;

        return facilityId && facilityName ? (
          <RoutingLink to={linkToFacilityDetail(facilityId)}>
            {facilityName}
          </RoutingLink>
        ) : (
          "-"
        );
      },
    },
    {
      label: "Calculation",
      key: "analytics_calculator_id",
      isSortable: true,
      renderComponent: ({ item }) => {
        const calculator = calculators.find(
          ({ id }) => id === item.analytics_calculator_id
        );

        return (
          <DataTable.DataRow.TextCell
            value={
              calculator?.versions.find(
                ({ version }) => version === calculator?.default_version
              )?.title ?? "-"
            }
          />
        );
      },
    },
  ];
  const actionRow = [
    <PermissionAwareExportButton
      key="export-estimation-methods"
      onClick={exportEstimationMethods}
      isExporting={isExporting}
    />,
    isDataIngestionEnabled ? (
      <ImportDataAction
        key="import-estimation-methods"
        resource={ResourceDefinitions.estimation_method}
      />
    ) : null,
  ];

  return (
    <>
      <BulkUpdateProgressBanner
        jobStorageKey={bulkUpdateEstimationMethodStatusJobStorageKey}
        setSelectedItems={setSelectedEstimationMethods}
        setIsRunning={setIsUpdating}
        setIsSelectionInverted={setIsSelectionInverted}
        invalidate={() => {
          queryClient.invalidateQueries({
            queryKey: [
              ESTIMATION_METHODS_QUERY_KEY,
              "list",
              estimationMethodParams,
            ],
          });
        }}
        subject={{
          singular: ResourceDefinitions.estimation_method.label.singular,
          plural: ResourceDefinitions.estimation_method.label.plural,
        }}
        action={(job) =>
          job?.input.updateBody.status === EstimationMethodStatus.INACTIVE
            ? "inactivate"
            : "activate"
        }
      />
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{
          actionRow,
          title: `${
            isFilteredByEntity
              ? "All"
              : toTitleCaseString(assetType, { intent: "ui" })
          } ${ResourceDefinitions.estimation_method.label.plural}`,
          titleDecorator: (
            <EstimationMethodTableTitleDecorationFilterArea
              filterConfigStorageKey={filterConfigStorageKey}
            />
          ),
        }}
        dataTableProps={{
          intent: "simplicity-first",
          headers,
          items: data?.data ?? [],
          getItemId: (item) => item.id,
          getItemBulkActions: ({ buttonProps }) => [
            <IsFeatureAvailable
              key="delete"
              featureFlagQuery="core:estimation_methods"
              permissionQuery="estimation_methods:write"
              fallbackChildren={null}
            >
              <Button
                {...buttonProps}
                onClick={() => setIsUpdateEstimationStatusDialogOpen(true)}
                isLoading={isUpdating}
              >
                Update Status
              </Button>
            </IsFeatureAvailable>,
          ],
          selected: selectedEstimationMethods,
          isSelectionInverted: isSelectionInverted,
          onSelectionChange: (value, shouldInvertSelection) => {
            setSelectedEstimationMethods(value);
            setIsSelectionInverted(shouldInvertSelection ?? false);
          },
          isLoading,
          isFluid: false,
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            total: data?.total_entries,
            pageSize: tableState.pageSize,
          },
        }}
        filterComponent={
          <EstimationMethodsTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
            assetType={assetType}
          />
        }
      />
      <UpdateEntityStatusDialog
        isOpen={isUpdateEstimationStatusDialogOpen}
        onClose={() => {
          setIsUpdateEstimationStatusDialogOpen(false);
        }}
        onSubmit={handleBulkUpdateEstimationMethodStatus}
        itemCount={
          isSelectionInverted
            ? (data?.total_entries ?? 0)
            : Object.keys(selectedEstimationMethods).length
        }
        subject={{
          singular: ResourceDefinitions.estimation_method.label.singular,
          plural: ResourceDefinitions.estimation_method.label.plural,
        }}
        warningMessage="This will delete all associated calculation results and inputs."
      />
    </>
  );
};
